import { EV_TARIFF_SCOPE_LABELS, EV_TARIFF_SCOPES, type EvTariffView } from "@station/contracts";
import { Decimal } from "@station/core";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Calculator, Pencil, Plus, PowerOff } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { describeBand, formatRate, powerClassText, tariffState } from "../../../features/ev/ev-values.js";
import { useEvPermissions, useEvPickers, useEvTariffs } from "../../../features/ev/queries.js";
import { EvPage, TariffStateBadge } from "../../../features/ev/shared.js";
import {
  DeactivateTariffDialog,
  TariffDialog,
  TariffPreviewDialog,
} from "../../../features/ev/tariff-dialogs.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object({
  companyId: z.string().uuid().optional().catch(undefined),
  stationId: z.string().uuid().optional().catch(undefined),
  scopeKind: z.enum(EV_TARIFF_SCOPES).optional().catch(undefined),
  all: z.boolean().optional().catch(undefined),
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/sarj/tarifeler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: TariffsRoute,
});

/**
 * Şarj tarifeleri (Prompt 19 web, `GET /ev/tariffs`): kapsam (şirket / istasyon / güç sınıfı / ünite), geçerlilik, kWh /
 * dakika / bekleme ücreti, saat bantları ve yürürlük durumu (yürürlükte / başlayacak / süresi doldu / devre dışı). Oluştur /
 * düzenle / devre dışı bırak `ev.manage` (sunucunun `canEdit`'i); tarife deneme `ev.read`. İstasyon süzgeci "bu istasyonu
 * kapsayan" tarifeleri (şirket varsayılanı dâhil) getirir.
 */
function TariffsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useEvPermissions();
  const pickers = useEvPickers(permissions.enabled && permissions.canRead);
  const [dialog, setDialog] = useState<
    | { kind: "edit"; tariff: EvTariffView | null }
    | { kind: "preview"; tariff: EvTariffView | null }
    | { kind: "deactivate"; tariff: EvTariffView }
    | null
  >(null);
  const search = Route.useSearch();
  const companyId = search.companyId ?? pickers.data?.companies[0]?.id ?? null;
  return (
    <EvPage
      title={t("menu.evTariffs")}
      description={t("ev.tariffs.description")}
      actions={
        pickers.data ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setDialog({ kind: "preview", tariff: null })}>
              <Calculator aria-hidden="true" />
              {t("ev.preview.open")}
            </Button>
            {permissions.canManage && companyId ? (
              <Button onClick={() => setDialog({ kind: "edit", tariff: null })}>
                <Plus aria-hidden="true" />
                {t("ev.tariffs.add")}
              </Button>
            ) : null}
          </div>
        ) : null
      }
    >
      {pickers.error ? (
        <Alert tone="danger">{errorMessage(pickers.error)}</Alert>
      ) : !pickers.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <>
          <TariffsBody
            search={search}
            companies={pickers.data.companies}
            stations={pickers.data.stations}
            onEdit={(tariff) => setDialog({ kind: "edit", tariff })}
            onPreview={(tariff) => setDialog({ kind: "preview", tariff })}
            onDeactivate={(tariff) => setDialog({ kind: "deactivate", tariff })}
          />
          <TariffDialog
            open={dialog?.kind === "edit"}
            onOpenChange={(open) => !open && setDialog(null)}
            tariff={dialog?.kind === "edit" ? dialog.tariff : null}
            pickers={pickers.data}
            defaultCompanyId={companyId ?? ""}
          />
          <TariffPreviewDialog
            open={dialog?.kind === "preview"}
            onOpenChange={(open) => !open && setDialog(null)}
            tariff={dialog?.kind === "preview" ? dialog.tariff : null}
            pickers={pickers.data}
          />
          {dialog?.kind === "deactivate" ? (
            <DeactivateTariffDialog
              open
              onOpenChange={(open) => !open && setDialog(null)}
              tariff={dialog.tariff}
            />
          ) : null}
        </>
      )}
    </EvPage>
  );
}

function TariffsBody({
  search,
  companies,
  stations,
  onEdit,
  onPreview,
  onDeactivate,
}: {
  search: Search;
  companies: Array<{ id: string; name: string }>;
  stations: Array<{ id: string; code: string; name: string; companyId: string }>;
  onEdit: (tariff: EvTariffView) => void;
  onPreview: (tariff: EvTariffView) => void;
  onDeactivate: (tariff: EvTariffView) => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSearch = (patch: Partial<Search>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });
  const filters = {
    companyId: search.companyId,
    stationId: search.stationId,
    scopeKind: search.scopeKind,
    includeInactive: search.all ?? false,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useEvTariffs(filters, pages.cursor);
  const now = new Date();
  const weekdays = useMemo(() => [1, 2, 3, 4, 5, 6, 7].map((day) => t(`ev.weekdays.${day}`)), [t]);

  const columns = useMemo<DataTableColumn<EvTariffView>[]>(
    () => [
      {
        id: "name",
        header: t("ev.tariffs.fields.name"),
        hideable: false,
        exportValue: (row) => row.name,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="font-medium">{row.name}</span>
            <span className="text-xs text-muted-foreground">{row.energyProductName}</span>
          </span>
        ),
      },
      {
        id: "scope",
        header: t("ev.tariffs.fields.scope"),
        exportValue: (row) => EV_TARIFF_SCOPE_LABELS[row.scopeKind],
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{EV_TARIFF_SCOPE_LABELS[row.scopeKind]}</span>
            <span className="text-xs text-muted-foreground">
              {[
                row.stationName,
                row.chargerName,
                row.scopeKind === "POWER_CLASS" ? powerClassText(row) : null,
                row.scopeKind === "POWER_CLASS" && !row.stationName ? t("ev.tariffs.allStations") : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
        ),
      },
      {
        id: "validity",
        header: t("ev.tariffs.fields.validity"),
        exportValue: (row) => `${row.validFrom} – ${row.validTo ?? ""}`,
        cell: (row) => (
          <span className="grid gap-0.5 text-xs tabular">
            <span>{formatDateTime(row.validFrom)}</span>
            <span className="text-muted-foreground">
              {row.validTo
                ? t("ev.tariffs.until", { at: formatDateTime(row.validTo) })
                : t("ev.tariffs.openEnded")}
            </span>
          </span>
        ),
      },
      {
        id: "energyPrice",
        header: t("ev.tariffs.fields.energyPrice"),
        align: "right",
        exportValue: (row) => row.energyPrice,
        cell: (row) => (
          <span className="grid justify-items-end gap-0.5">
            <span className="tabular whitespace-nowrap">{formatRate(row.energyPrice, "KWH")}</span>
            {row.bands.map((band) => (
              <span
                key={`${band.weekdays.join(",")}-${band.from}`}
                className="text-xs whitespace-nowrap text-muted-foreground"
              >
                {describeBand(band, weekdays, t("ev.tariffs.everyDay"))}
              </span>
            ))}
          </span>
        ),
      },
      {
        id: "extras",
        header: t("ev.tariffs.extras"),
        exportValue: (row) => `${row.minutePrice} / ${row.idlePrice}`,
        cell: (row) => (
          <span className="grid gap-0.5 text-xs">
            <span>
              {t("ev.tariffs.minuteShort")}: {formatRate(row.minutePrice, "MINUTE")}
            </span>
            <span>
              {t("ev.tariffs.idleShort")}: {formatRate(row.idlePrice, "MINUTE")}
              {Decimal.from(row.idlePrice).isPositive()
                ? ` · ${t("ev.tariffs.graceShort", { count: row.idleGraceMinutes })}${
                    row.idleCap
                      ? ` · ${t("ev.tariffs.capShort", { cap: Decimal.from(row.idleCap).formatTr(2) })}`
                      : ""
                  }`
                : ""}
            </span>
          </span>
        ),
      },
      {
        id: "state",
        header: t("common.status"),
        exportValue: (row) => t(`ev.tariffs.state.${tariffState(row, now)}`),
        cell: (row) => <TariffStateBadge state={tariffState(row, now)} />,
      },
      {
        id: "actions",
        header: t("common.actions"),
        hideable: false,
        cell: (row) => (
          <span className="flex flex-wrap gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onPreview(row)}
              aria-label={t("ev.preview.openFor", { name: row.name })}
            >
              <Calculator aria-hidden="true" />
              {t("ev.preview.short")}
            </Button>
            {row.canEdit && row.isActive ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onEdit(row)}
                  aria-label={t("ev.tariffs.editFor", { name: row.name })}
                >
                  <Pencil aria-hidden="true" />
                  {t("common.edit")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDeactivate(row)}
                  aria-label={t("ev.tariffs.deactivateFor", { name: row.name })}
                >
                  <PowerOff aria-hidden="true" />
                  {t("ev.tariffs.deactivate")}
                </Button>
              </>
            ) : null}
          </span>
        ),
      },
    ],
    // `now` yalnızca durum rozeti içindir; sorgu tazelenince yeniden hesaplanır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, weekdays, onEdit, onPreview, onDeactivate, list.dataUpdatedAt],
  );

  const visibleStations = stations.filter(
    (station) => !search.companyId || station.companyId === search.companyId,
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        {companies.length > 1 ? (
          <FormField label={t("ev.fields.company")} className="w-56">
            {(control) => (
              <Select
                {...control}
                value={search.companyId ?? ""}
                onChange={(event) =>
                  setSearch({ companyId: event.target.value || undefined, stationId: undefined })
                }
              >
                <option value="">{t("common.all")}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
        {visibleStations.length > 1 ? (
          <FormField label={t("ev.tariffs.coveringStation")} className="w-64">
            {(control) => (
              <Select
                {...control}
                value={search.stationId ?? ""}
                onChange={(event) => setSearch({ stationId: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {visibleStations.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.code} {station.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
        <FormField label={t("ev.tariffs.fields.scope")} className="w-56">
          {(control) => (
            <Select
              {...control}
              value={search.scopeKind ?? ""}
              onChange={(event) =>
                setSearch({ scopeKind: (event.target.value || undefined) as Search["scopeKind"] })
              }
            >
              <option value="">{t("common.all")}</option>
              {EV_TARIFF_SCOPES.map((scope) => (
                <option key={scope} value={scope}>
                  {EV_TARIFF_SCOPE_LABELS[scope]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <label className="flex min-h-10 items-center gap-2 text-sm">
          <Checkbox
            checked={search.all ?? false}
            onChange={(event) => setSearch({ all: event.target.checked ? true : undefined })}
          />
          {t("ev.tariffs.includeInactive")}
        </label>
      </Card>
      <DataTable
        caption={t("ev.tariffs.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Calculator />}
            title={t("ev.tariffs.emptyTitle")}
            description={t("ev.tariffs.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
    </>
  );
}
